import { FC, useMemo } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'

import { ActionButton, Subheader, VStack } from '@revolut-internal/ui-kit'

import { Security } from 'modules/security/security'

import { useQueueHealthPolicy } from '../QueueHealthPolicyProvider'
import { QueueHealthPolicyThreshold } from '../QueueHealthPolicyThreshold/QueueHealthPolicyThreshold'
import { QueueHealthPolicyThresholdSkeleton } from '../QueueHealthPolicyThreshold/QueueHealthPolicyThresholdSkeleton'
import { AlertingPolicyForm } from '../types'

import { EMPTY_THRESHOLD } from './contst'

type QueueHealthPolicyFormMetricsProps = {
  isReadonly: boolean
  onTriggerThresholdDeletion: VoidFunction
}
export const QueueHealthPolicyFormMetrics: FC<QueueHealthPolicyFormMetricsProps> = ({
  isReadonly,
  onTriggerThresholdDeletion,
}) => {
  const { metrics, isThresholdPending } = useQueueHealthPolicy()
  const { control } = useFormContext<AlertingPolicyForm>()
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'thresholds',
  })

  const metricsLookupMap = useMemo(() => {
    if (!metrics) {
      return new Map()
    }

    return new Map(metrics?.map(metric => [metric.id, metric.label]))
  }, [metrics])

  const fieldsWithAvailableMetricsCount = useMemo(
    () =>
      fields.filter(field => metricsLookupMap.has(field.metricId) || !field.metricId)
        .length,
    [fields, metricsLookupMap],
  )

  const handleThresholdDelete = (index: number) => {
    if (fieldsWithAvailableMetricsCount === 1) {
      onTriggerThresholdDeletion()

      return
    }

    remove(index)
  }

  return (
    <>
      <Subheader>
        <Subheader.Title>Rules</Subheader.Title>
        {!isReadonly && (
          <Subheader.Side>
            <ActionButton
              variant="accent"
              useIcon="Plus"
              onClick={() => append(EMPTY_THRESHOLD)}
            >
              Add
            </ActionButton>
          </Subheader.Side>
        )}
      </Subheader>

      <VStack space="s-16">
        {isThresholdPending && <QueueHealthPolicyThresholdSkeleton />}
        {/* use original fields to keep the index */}
        {fields.map((field, index) =>
          metricsLookupMap.has(field.metricId) || !field.metricId ? (
            <QueueHealthPolicyThreshold
              key={field.id}
              fieldName="thresholds"
              index={index}
              allowsDelete={
                Security.canDeletePolicy || fieldsWithAvailableMetricsCount > 1
              }
              onDelete={() => handleThresholdDelete(index)}
            />
          ) : null,
        )}
      </VStack>
    </>
  )
}
